import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/api-client';
import { langName } from '../lib/lang-names';

interface LangStatus {
  total: number;
  missing: number;
  complete: number;
  mg: string;
}

interface StatusResponse {
  languages: Record<string, LangStatus>;
  scanned: number;
}

interface DeployResponse {
  language: string;
  total: number;
  updated: number;
  skipped: number;
  errors: number;
}

export default function Transcription() {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<DeployResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    apiClient<StatusResponse>('/api/admin/transcription/status')
      .then((d) => { setData(d); window.scrollTo(0, 0); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const deploy = useCallback(async (lang: string) => {
    setDeploying(lang);
    setLastResult(null);
    try {
      const result = await apiClient<DeployResponse>('/api/admin/transcription/deploy', {
        method: 'POST',
        body: JSON.stringify({ language: lang }),
      });
      setLastResult(result);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Deploy failed');
    } finally {
      setDeploying(null);
    }
  }, [refresh]);

  const deployAll = useCallback(async () => {
    if (!data) return;
    const langs = Object.entries(data.languages)
      .filter(([, s]) => s.missing > 0)
      .map(([code]) => code);
    for (const lang of langs) {
      await deploy(lang);
    }
  }, [data, deploy]);

  if (loading) return <div className="p-4 md:p-8 text-zinc-500">Loading transcription status... (scanning all published dialogs)</div>;
  if (error) return <div className="p-4 md:p-8"><div className="text-red-400 bg-red-900/20 border border-red-800 rounded p-4">Error: {error}</div></div>;
  if (!data) return <div className="p-4 md:p-8 text-zinc-500">No data returned.</div>;

  const entries = Object.entries(data.languages).sort(([a], [b]) => a.localeCompare(b));
  const withGaps = entries.filter(([, s]) => s.missing > 0);
  const totalMissing = withGaps.reduce((sum, [, s]) => sum + s.missing, 0);
  const totalComplete = entries.reduce((sum, [, s]) => sum + s.complete, 0);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl md:text-2xl font-semibold text-white">Transcription Status</h1>
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-sm text-zinc-500">{data.scanned} published dialogs scanned</span>
          {withGaps.length > 0 && (
            <button
              onClick={deployAll}
              disabled={deploying !== null}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-zinc-700 text-white text-sm font-medium rounded transition-colors"
            >
              {deploying ? `Deploying ${deploying}...` : `Deploy All (${totalMissing} dialogs)`}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card label="Languages Tracked" value={entries.length} />
        <Card label="Fully Transcribed" value={totalComplete} accent="text-teal-400" />
        <Card label="Missing Transcription" value={totalMissing} accent={totalMissing > 0 ? 'text-amber-400' : 'text-teal-400'} />
        <Card label="Languages with Gaps" value={withGaps.length} accent={withGaps.length > 0 ? 'text-amber-400' : 'text-teal-400'} />
      </div>

      {lastResult && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-sm">
          <span className="text-teal-400 font-medium">{lastResult.language}</span>: {lastResult.updated} updated, {lastResult.skipped} already done, {lastResult.errors} errors
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[500px]">
          <thead>
            <tr>
              <th className="text-left px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700">Language</th>
              <th className="px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 text-center">Published</th>
              <th className="px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 text-center">Complete</th>
              <th className="px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 text-center">Missing</th>
              <th className="px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 text-center">Coverage</th>
              <th className="px-3 py-2 text-zinc-400 font-medium border-b border-zinc-700 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([code, s]) => {
              const pct = s.total > 0 ? Math.round((s.complete / s.total) * 100) : 0;
              const isFull = s.missing === 0;
              return (
                <tr key={code} className="hover:bg-zinc-800/50">
                  <td className="px-3 py-2 border-b border-zinc-800">
                    <span className="text-white font-mono text-xs">{code}</span>
                    <span className="text-zinc-500 ml-2 text-xs">{langName(code)}</span>
                  </td>
                  <td className="px-3 py-2 text-center border-b border-zinc-800 text-zinc-300">{s.total}</td>
                  <td className="px-3 py-2 text-center border-b border-zinc-800 text-teal-400">{s.complete}</td>
                  <td className={`px-3 py-2 text-center border-b border-zinc-800 ${s.missing > 0 ? 'text-amber-400 font-medium' : 'text-zinc-500'}`}>
                    {s.missing || '-'}
                  </td>
                  <td className="px-3 py-2 text-center border-b border-zinc-800">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-16 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${isFull ? 'bg-teal-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`text-xs ${isFull ? 'text-teal-400' : 'text-zinc-400'}`}>{pct}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center border-b border-zinc-800">
                    {s.missing > 0 ? (
                      <button
                        onClick={() => deploy(code)}
                        disabled={deploying !== null}
                        className="px-3 py-1 bg-teal-700 hover:bg-teal-600 disabled:bg-zinc-700 text-white text-xs rounded transition-colors"
                      >
                        {deploying === code ? 'Deploying...' : `Deploy ${s.missing}`}
                      </button>
                    ) : (
                      <span className="text-teal-500 text-xs">Done</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {entries.length === 0 && (
        <p className="text-zinc-500 text-center py-8">No published dialogs require transcription.</p>
      )}
    </div>
  );
}

function Card({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
      <div className="text-zinc-400 text-xs uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${accent ?? 'text-white'}`}>{value}</div>
    </div>
  );
}
